import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription, filter } from 'rxjs';
import { UserInfo, LogStatus, LogError, RunView } from '@memberjunction/core';
import { UserService } from 'src/app/services/user.service';
import { DataFetchService } from 'src/app/services/data-fetch.service';
import { SummaryCard, DataGridColumn, DataGridExportOptions } from 'src/app/utils/interfaces';
import { ChapterOfficersEntity } from 'mj_generatedentities';
import { AcsDataGridComponent } from '../shared/acs-data-grid/acs-data-grid';
import { DropdownOption } from '../shared/acs-button/acs-button';
import { GridExportOptions, ChapterOfficerGridColumns } from 'src/app/utils/grid-constants';
import { EntityNames } from 'src/app/utils/constants';
import { ActiveUserService } from 'src/app/services/active-user.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { UserCommitteeAccessService } from 'src/app/services/user-committee-access.service';

@Component({
  selector: 'acs-chapter-officers',
  templateUrl: './chapter-officers.html',
  styleUrls: ['../../../styles/_page-layout.scss', './chapter-officers-specific.scss']
})
export class ChapterOfficersComponent implements OnInit, OnDestroy {
  @ViewChild('officerGrid') officerGrid!: AcsDataGridComponent;
  
  private subscription = new Subscription();
  readonly entityName = EntityNames.ChapterOfficers;

  isLoading = true;
  error: string | null = null;

  // Grid Configuration
  gridColumns: DataGridColumn[] = ChapterOfficerGridColumns;
  officers: ChapterOfficersEntity[] = [];
  currentUser!: UserInfo;
  chapterId?: string;

  // Export dropdown options
  exportOptions: DropdownOption[] = GridExportOptions;
  
  constructor(
    private userService: UserService,
    private dataFetchService: DataFetchService,
    private userAuthService: UserAuthService,
    private activeUserService: ActiveUserService,
    private userCommitteeAccessService: UserCommitteeAccessService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.userService.getUserComplete$.pipe(filter(complete => complete)).subscribe(() => {
        this.initializeComponent();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async initializeComponent(): Promise<void> {
    this.currentUser = this.userService.getUserInfo();
    // Check if user has a specific chapter assignment
    this.checkUserChapterAccess();
    this.loadOfficersCount();
    this.isLoading = false;
  }

  private checkUserChapterAccess(): void {
    // In a real implementation, this would check user's chapter access
    // For now, we'll allow viewing all officers
    this.chapterId = undefined;
  }

  onServerError(error: any): void {
    LogError('Error loading chapter officers:', undefined, error);
    this.error = 'Failed to load chapter officer data. Please try again.';
  }

  onExportOptionSelected(option: DropdownOption): void {
    const format = option.value as 'csv' | 'excel' | 'pdf';
    
    // Use the officerGrid to perform the export
    if (this.officerGrid) {
      this.officerGrid.onExport(format);
      LogStatus(`Exporting all officers as ${format.toUpperCase()}`);
    } else {
      alert('Grid not available for export. Please try again.');
    }
  }

  private _totalOfficersCount: number = 0;
  // Helper methods for template
  get TotalOfficers(): number {
    return this._totalOfficersCount;
  }

  private async loadOfficersCount(): Promise<void> {
    
    const rv = new RunView();
    const result = await rv.RunView<ChapterOfficersEntity>({
      EntityName: this.entityName,
      ResultType: 'count_only',
      ExtraFilter: this.buildCommitteeAccessFilter()
    });

    this._totalOfficersCount = result.TotalRowCount || 0;
  }

  
  /**
   * Build committee access filter based on user's role and committee assignments
   * Chapter Admins see only their committee data, Staff see all data
   *
   * @param committeeColumnName - Name of the committee column in the SQL query (defaults to 'CommitteeID')
   * @returns SQL filter string for committee access, or empty string if no filter needed
   */
  buildCommitteeAccessFilter(committeeColumnName: string = 'CommitteeID'): string {
    try {
        // Get the active user for logging
        const activeUser = this.activeUserService.getActiveUser();
        if (!activeUser) {
          LogError('buildCommitteeAccessFilter: No active user found for committee filtering');
          return '';
        }

        // Check user role using UserAuthService - Staff users have no restrictions
        if (this.userAuthService.isStaff()) {
          LogStatus(`buildCommitteeAccessFilter: User ${activeUser.Name} has Staff role - no committee filtering applied`);
          return '';
        }

        // Check if user has no access at all
        if (!this.userAuthService.hasAccess()) {
          LogStatus(`buildCommitteeAccessFilter: User ${activeUser.Name} has no access - returning restrictive filter`);
          return `${committeeColumnName} = ''`;
        }

        // For Chapter Admins and other roles with access, apply committee access filtering
        const committeeFilter = this.userCommitteeAccessService.getCommitteeAccessFilter(committeeColumnName);

        if (committeeFilter) {
          const userRole = this.userAuthService.getUserRole();
          LogStatus(`buildCommitteeAccessFilter: Applied committee access filter for ${activeUser.Name} (${userRole}): ${committeeFilter}`);
        } else {
          LogStatus(`buildCommitteeAccessFilter: No committee access restrictions for ${activeUser.Name}`);
        }

        return committeeFilter;

    } catch (error) {
      LogError('buildCommitteeAccessFilter: Error building committee access filter', undefined, error);
      // Return restrictive filter on error for security
      return `${committeeColumnName} = ''`;
    }
  }
}